import { useDeferredValue, useEffect, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Clipboard, KeyRound, Search, Star, X } from "lucide-react";

import { useUsernameSuggestion, usernameHint } from "../../hooks/useUsernameSuggestion";
import { studentService, type Parent } from "../../services/studentService";
import { DEFAULT_SCHOOL_EMAIL_DOMAIN, schoolEmail } from "../../utils/schoolEmail";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { RELATIONSHIPS } from "./parentsLogic";

/** Adds a parent with a portal login, or edits one. The login's address is its username on the school domain. */
export function ParentForm({ record, forLearner, busy, error, onSave, onClose }: {
  record?: Parent;
  /** Set when adding a parent from a learner who has none; they are linked straight after. */
  forLearner?: { full_name: string };
  busy: boolean;
  error?: string;
  onSave: (data: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const [firstName, setFirstName] = useState(record?.user.first_name ?? "");
  const [lastName, setLastName] = useState(record?.user.last_name ?? "");
  const [username, setUsername] = useState(record?.user.username ?? "");
  // An existing login is theirs already and is not renamed underneath them.
  const [usernameEdited, setUsernameEdited] = useState(Boolean(record));
  const { base, suggested } = useUsernameSuggestion(firstName, lastName);
  useEffect(() => {
    if (!usernameEdited) setUsername(suggested || base);
  }, [base, suggested, usernameEdited]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const text = (key: string) => String(form.get(key) ?? "").trim();
    const data: Record<string, unknown> = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      username: username.trim(),
      phone_number: text("phone_number"),
      relationship: text("relationship"),
      emergency_contact: text("emergency_contact"),
      occupation: text("occupation"),
      workplace: text("workplace"),
      address: text("address"),
    };
    if (!record) data.password = text("password");
    onSave(data);
  };

  return (
    <FormModal
      id="parent-form"
      title={record ? `Edit ${record.full_name || record.user.username}` : forLearner ? `Add a parent for ${forLearner.full_name}` : "Add a parent or guardian"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : forLearner ? "Add and link" : "Add parent"}
      note={record ? "To change the password, use Reset password on their record." : "They change the temporary password the first time they sign in."}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Name" divided={false} columns={2}>
        <Field label="First name">
          <input required autoFocus value={firstName} onChange={(event) => setFirstName(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Last name">
          <input required value={lastName} onChange={(event) => setLastName(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Relationship to the child">
          <select required name="relationship" defaultValue={record?.relationship ?? "guardian"} className="input mt-1">
            {RELATIONSHIPS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
      </FormSection>

      <FormSection title="Portal login" description={`The parent signs in to the family portal with this username; their email is the username @${DEFAULT_SCHOOL_EMAIL_DOMAIN}.`} columns={2}>
        <Field label="Username" hint={usernameHint(base, suggested, usernameEdited)}>
          <input required value={username} onChange={(event) => { setUsernameEdited(true); setUsername(event.target.value); }} className="input mt-1" placeholder="e.g. mruth" autoComplete="off" />
        </Field>
        <Field label="Email" hint="Set from the username.">
          <input readOnly tabIndex={-1} value={schoolEmail(username)} className="input mt-1 bg-gray-50 text-gray-600" />
        </Field>
        {!record && (
          <Field label="Temporary password" hint="At least 8 characters. You'll see it once more after saving, to hand over.">
            <input required minLength={8} name="password" type="text" autoComplete="new-password" className="input mt-1 font-mono" />
          </Field>
        )}
      </FormSection>

      <FormSection title="Contact" columns={2}>
        <Field label="Phone" optional hint="The number the school calls and texts.">
          <input name="phone_number" type="tel" defaultValue={record?.user.phone_number ?? ""} className="input mt-1" placeholder="+256 7XX XXX XXX" />
        </Field>
        <Field label="Emergency contact" optional hint="Another number, if this one can't be reached.">
          <input name="emergency_contact" type="tel" defaultValue={record?.emergency_contact ?? ""} className="input mt-1" />
        </Field>
        <Field label="Address" optional className="sm:col-span-2">
          <textarea name="address" rows={2} defaultValue={record?.address ?? ""} className="input mt-1" placeholder="Village, parish, sub-county, district" />
        </Field>
      </FormSection>

      <FormSection title="Work" columns={2}>
        <Field label="Occupation" optional><input name="occupation" defaultValue={record?.occupation ?? ""} className="input mt-1" /></Field>
        <Field label="Workplace" optional><input name="workplace" defaultValue={record?.workplace ?? ""} className="input mt-1" /></Field>
      </FormSection>
    </FormModal>
  );
}

type Linked = { id: number; full_name: string; student_id: string; class_name?: string | null; primary: boolean };

/** The children a parent is linked to, and which of them the parent is the first contact for. */
export function LinkChildrenForm({ parent, busy, error, onSave, onClose }: {
  parent: Parent;
  busy: boolean;
  error?: string;
  onSave: (data: { student_ids: number[]; primary_student_ids: number[] }) => void;
  onClose: () => void;
}) {
  const [linked, setLinked] = useState<Linked[]>(() => (parent.children_data ?? []).map((child) => ({ ...child, primary: child.is_primary })));
  const [search, setSearch] = useState("");
  const query = useDeferredValue(search.trim());
  const found = useQuery({
    queryKey: ["parents", "learner-search", query],
    queryFn: () => studentService.getStudents({ search: query, status: "active", ordering: "user__first_name", page_size: 15 }),
    enabled: query.length >= 2,
  });
  const add = (row: Linked) => setLinked((current) => (current.some((item) => item.id === row.id) ? current : [...current, row]));
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave({ student_ids: linked.map((row) => row.id), primary_student_ids: linked.filter((row) => row.primary).map((row) => row.id) });
  };
  return (
    <FormModal id="link-children-form" title={`Children of ${parent.full_name || parent.user.username}`} busy={busy} error={error} saveLabel="Save links" note="The primary contact is who the school calls first about that child." onClose={onClose} onSubmit={submit}>
      <FormSection title="Linked" divided={false} columns={1}>
        {linked.length ? (
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
            {linked.map((row) => (
              <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm">
                <span>
                  <span className="font-medium text-gray-900">{row.full_name}</span>
                  <span className="ml-2 text-xs text-gray-500">{row.student_id} · {row.class_name || "no class"}</span>
                </span>
                <span className="flex items-center gap-2">
                  <button type="button" aria-pressed={row.primary} onClick={() => setLinked((current) => current.map((item) => (item.id === row.id ? { ...item, primary: !item.primary } : item)))} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${row.primary ? "bg-primary-100 text-primary-800" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    <Star className="h-3 w-3" /> {row.primary ? "Primary contact" : "Make primary"}
                  </button>
                  <button type="button" onClick={() => setLinked((current) => current.filter((item) => item.id !== row.id))} className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600" aria-label={`Unlink ${row.full_name}`}>
                    <X className="h-4 w-4" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-lg border border-dashed border-gray-300 py-4 text-center text-sm text-gray-500">No children linked yet. Find them below.</p>
        )}
      </FormSection>
      <FormSection title="Add a child" columns={1}>
        <Field as="div" label="Find a learner" hint="Type at least two letters of a name, student ID or admission number.">
          <div className="relative mt-1">
            <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} className="input pl-8" placeholder="Search learners in school" />
          </div>
        </Field>
        {query.length >= 2 && (
          <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
            {found.isLoading && <li className="px-3 py-2 text-sm text-gray-500">Searching…</li>}
            {found.data?.results.map((student) => {
              const already = linked.some((row) => row.id === student.id);
              return (
                <li key={student.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                  <span>
                    <span className="font-medium text-gray-900">{student.full_name}</span>
                    <span className="ml-2 text-xs text-gray-500">{student.student_id} · {student.current_class_name || "no class"}{student.parents_data?.length ? ` · ${student.parents_data.length} parent${student.parents_data.length === 1 ? "" : "s"} on record` : " · no parent on record"}</span>
                  </span>
                  <button type="button" disabled={already} onClick={() => add({ id: student.id, full_name: student.full_name, student_id: student.student_id, class_name: student.current_class_name, primary: !student.primary_parent })} className="btn btn-outline h-8 gap-1 text-xs">
                    {already ? <><Check className="h-3.5 w-3.5" /> Linked</> : "Link"}
                  </button>
                </li>
              );
            })}
            {found.data && !found.data.results.length && <li className="px-3 py-2 text-sm text-gray-500">No learner in school matches.</li>}
          </ul>
        )}
      </FormSection>
    </FormModal>
  );
}

/** A login's temporary password, shown once to hand over. */
export function CredentialsCard({ title, username, password, onDismiss }: { title: string; username: string; password: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div role="status" className="rounded-lg border border-green-200 bg-green-50 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-green-700" />
          <div>
            <h2 className="font-semibold text-green-900">{title}</h2>
            <p className="mt-0.5 text-sm text-green-800">Hand these over privately. This is the only time the password is shown; the parent chooses their own when they first sign in.</p>
          </div>
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="rounded p-1 text-green-800 hover:bg-green-100"><X className="h-4 w-4" /></button>
      </div>
      <dl className="mt-3 grid gap-1 rounded-md bg-white p-3 font-mono text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
        <dt className="text-gray-500">Username</dt><dd className="text-gray-900">{username}</dd>
        <dt className="text-gray-500">Password</dt><dd className="text-gray-900">{password}</dd>
      </dl>
      <button type="button" className="btn btn-outline mt-3 gap-1.5" onClick={() => { void navigator.clipboard?.writeText(`Username: ${username}\nPassword: ${password}`); setCopied(true); }}>
        <Clipboard className="h-4 w-4" /> {copied ? "Copied" : "Copy both"}
      </button>
    </div>
  );
}
